import { memo, useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';
import moment from 'moment';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import FormDateField from 'components/ui/form/FormDateField';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';

const StatBlock = ({ label, value, suffix, accent }) => (
  <div className='bg-slate-50 rounded-xl p-4 border-l-4' style={{ borderLeftColor: accent }}>
    <div className='text-xs font-medium text-gray-500'>{label}</div>
    <div className='text-[20px] font-bold text-gray-800'>
      {value ? Number(Number(value).toFixed(2)) : 0} {suffix}
    </div>
  </div>
);

const ProfileStats = () => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  const openContract = (contractId) => {
    if (contractId) navigate(`/admin/contract/view/${contractId}`);
  };

  const formik = useFormik({
    initialValues: { date: moment().format('YYYY-MM-DD') },
    onSubmit: () => {},
  });

  const date = formik.values.date;

  // Profile + hr info
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['/admin/profile/edit'],
    queryFn: async () => {
      const res = await axiosPrivate.get('/admin/profile/edit');
      return res?.data?.data;
    },
    retry: false,
  });

  const hrId = profile?.hr?.object_id;
  const userId = profile?.id;

  // Worked time data
  const { data: hrData, isLoading: hrLoading } = useQuery({
    queryKey: ['/hr/onlyuser', hrId, date],
    queryFn: async () => {
      const res = await axiosPrivate.get(`/hr/onlyuser/${hrId}?date=${date}`);
      return res?.data?.data;
    },
    enabled: !!hrId && !!date,
    retry: false,
  });

  // KPI data
  const [kpi, setKpi] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [expandedKpi, setExpandedKpi] = useState(() => new Set());

  const toggleKpiExpand = (idx) => {
    setExpandedKpi((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  useEffect(() => {
    if (!userId || !date) return;
    const m = moment(date, 'YYYY-MM-DD');
    const from = m.clone().startOf('month').format('YYYY-MM-DD');
    const till = m.clone().endOf('month').format('YYYY-MM-DD');
    setKpiLoading(true);
    axiosPrivate
      .get(`/admin/staff-kpi/staff/${userId}?from=${from}&till=${till}`)
      .then((res) => setKpi(res?.data?.data || null))
      .catch(() => setKpi(null))
      .finally(() => setKpiLoading(false));
  }, [userId, date, axiosPrivate]);

  // /hr/onlyuser response: stats / salary / valute are at top level (no report wrapper)
  const stats = hrData?.stats;
  const salary = hrData?.salary;
  const valute = hrData?.valute;
  const percent = useMemo(() => {
    if (!stats?.planned_minutes) return 0;
    return (stats.total_minutes / stats.planned_minutes) * 100;
  }, [stats]);

  if (profileLoading) {
    return (
      <div className='flex justify-center py-12'>
        <CircularProgress size={32} />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      {/* Date filter */}
      <div className='bg-white base-border p-4 flex items-center gap-2'>
        <div className='flex-1'>
          <FormDateField
            delay={0}
            label={t('common.fields.date')}
            fieldName='date'
            formik={formik}
          />
        </div>
        <Button
          variant='outlined'
          size='small'
          onClick={() =>
            formik.setFieldValue('date', moment().format('YYYY-MM-DD'))
          }
        >
          Bugun
        </Button>
      </div>

      {/* Worked time */}
      <div className='bg-white base-border p-5'>
        <div className='text-base font-semibold mb-3 flex items-center gap-2'>
          <i className='bi bi-clock-history text-blue-500' />
          Ish vaqti
        </div>

        {!hrId ? (
          <div className='bg-amber-50 border border-amber-200 rounded-xl p-4 text-center text-amber-700 text-sm'>
            <i className='bi bi-exclamation-triangle mr-1' />
            {t('hr.connect.notConnected')}
          </div>
        ) : hrLoading ? (
          <div className='flex justify-center py-6'>
            <CircularProgress size={22} />
          </div>
        ) : stats ? (
          <>
            <div className='grid grid-cols-2 gap-2 mb-4'>
              <StatBlock
                label={t('boss.offices.workStatuses.total_minutes')}
                value={stats.total_minutes}
                suffix={t('common.fields.mins')}
                accent='#22c55e'
              />
              <StatBlock
                label={t('boss.offices.workStatuses.planned_minutes')}
                value={stats.planned_minutes}
                suffix={t('common.fields.mins')}
                accent='#6366f1'
              />
              <StatBlock
                label={t('boss.offices.workStatuses.overtime_minutes')}
                value={stats.overtime_minutes}
                suffix={t('common.fields.mins')}
                accent='#f59e0b'
              />
              <StatBlock
                label={t('boss.offices.workStatuses.under_minutes')}
                value={stats.under_minutes}
                suffix={t('common.fields.mins')}
                accent='#ef4444'
              />
            </div>

            {/* Salary progress */}
            <div className='flex items-center gap-2'>
              <div>0</div>
              <div className='bg-blue-100 h-3 grow rounded-full'>
                <div
                  style={{ width: `${Math.min(percent, 100)}%` }}
                  className='bg-blue-600 h-3 rounded-full'
                />
              </div>
              <div className='whitespace-nowrap flex flex-col items-end leading-tight'>
                <span className='text-green-600 font-semibold text-sm'>
                  <CurrencyFormat
                    value={Number(
                      (percent * (salary || 0)) / 100,
                    )}
                    suffix={` ${valute || ''}`}
                    decimalScale={2}
                  />
                </span>
                <span className='text-gray-500 text-xs'>
                  /{' '}
                  <CurrencyFormat
                    value={salary}
                    suffix={` ${valute || ''}`}
                    decimalScale={2}
                  />
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className='text-center text-gray-400 py-4 text-sm'>
            <i className='bi bi-exclamation-octagon mr-1' />
            {t('common.global.noDataFound')}
          </div>
        )}
      </div>

      {/* KPI */}
      <div className='bg-white base-border p-5'>
        <div className='text-base font-semibold mb-3 flex items-center gap-2'>
          <i className='bi bi-cash-coin text-green-500' />
          {t('settings.staffKpi.earnings.title')}
        </div>

        {kpiLoading ? (
          <div className='flex justify-center py-6'>
            <CircularProgress size={22} />
          </div>
        ) : kpi?.items?.length > 0 ? (
          <div className='flex flex-col gap-2'>
            {kpi.items.map((item, idx) => {
              const sources = item?.sources || [];
              const hasSources = sources.length > 0;
              const isExpanded = expandedKpi.has(idx);
              return (
                <div
                  key={idx}
                  className='rounded-lg border border-gray-200 bg-white overflow-hidden'
                >
                  <button
                    type='button'
                    onClick={() => hasSources && toggleKpiExpand(idx)}
                    className={`w-full flex items-center justify-between px-3 py-2 gap-2 ${
                      hasSources ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
                    }`}
                  >
                    <div className='flex flex-col gap-0.5 min-w-0 flex-1 text-left'>
                      <span className='font-medium text-sm text-gray-800 truncate'>
                        {item?.kpi?.type?.name || `#${item?.kpi?.type_id}`}
                      </span>
                      <span className='text-xs text-gray-500 flex items-center gap-1'>
                        {item?.kpi?.count_type_text} ·
                        <NumericFormat
                          value={item?.kpi?.price}
                          displayType='text'
                          thousandSeparator={item?.kpi?.count_type === 2 ? ' ' : ''}
                          decimalScale={2}
                        />
                        {item?.kpi?.count_type === 1 ? '%' : ''}
                      </span>
                    </div>
                    <div className='flex flex-col items-end text-sm font-medium shrink-0'>
                      {item?.earned_sum > 0 && (
                        <NumericFormat
                          value={item.earned_sum}
                          displayType='text'
                          thousandSeparator=' '
                          decimalScale={2}
                          suffix=' UZS'
                        />
                      )}
                      {item?.earned_valute > 0 && (
                        <NumericFormat
                          value={item.earned_valute}
                          displayType='text'
                          thousandSeparator=' '
                          decimalScale={2}
                          suffix=' $'
                        />
                      )}
                      {!item?.earned_sum && !item?.earned_valute && (
                        <span className='text-gray-400'>—</span>
                      )}
                    </div>
                    {hasSources && (
                      <i
                        className={`bi bi-chevron-${
                          isExpanded ? 'up' : 'down'
                        } text-gray-400 ml-1`}
                      />
                    )}
                  </button>
                  {isExpanded && hasSources && (
                    <div className='border-t border-gray-200 bg-gray-50 px-3 py-2 flex flex-col gap-1.5'>
                      {sources.map((src, sidx) => {
                        const clickable = !!src.contract_id;
                        return (
                          <div
                            key={sidx}
                            onClick={
                              clickable ? () => openContract(src.contract_id) : undefined
                            }
                            className={`flex items-center justify-between text-xs gap-2 ${
                              clickable
                                ? 'cursor-pointer hover:bg-gray-100 -mx-1 px-1 py-0.5 rounded'
                                : ''
                            }`}
                          >
                            <div className='flex flex-col min-w-0 flex-1'>
                              <span
                                className={`truncate ${
                                  clickable
                                    ? 'text-blue-600 hover:underline'
                                    : 'text-gray-700'
                                }`}
                              >
                                {src.label || `#${src.id}`}
                              </span>
                              <span className='text-gray-400'>
                                {src.event_date || '—'}
                              </span>
                            </div>
                            <div className='font-medium text-gray-800 shrink-0'>
                              <NumericFormat
                                value={src.earned}
                                displayType='text'
                                thousandSeparator=' '
                                decimalScale={2}
                                suffix={src.isvalute === 1 ? ' $' : ' UZS'}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <div className='flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 mt-1'>
              <span className='font-medium text-emerald-700 text-sm'>
                {t('common.table.total')}:
              </span>
              <div className='flex flex-col items-end font-semibold text-emerald-700 text-sm'>
                {kpi?.total_sum > 0 && (
                  <NumericFormat
                    value={kpi.total_sum}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                    suffix=' UZS'
                  />
                )}
                {kpi?.total_valute > 0 && (
                  <NumericFormat
                    value={kpi.total_valute}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                    suffix=' $'
                  />
                )}
                {!kpi?.total_sum && !kpi?.total_valute && <span>0 UZS</span>}
              </div>
            </div>
          </div>
        ) : (
          <div className='text-center text-gray-400 py-4 text-sm'>
            <i className='bi bi-exclamation-octagon mr-1' />
            {t('common.global.noDataFound')}
          </div>
        )}
      </div>

      {/* Kutilayotgan natijalar (hali shart bajarilmagan) */}
      {kpi?.items?.some((it) => (it?.pending?.length ?? 0) > 0) && (
        <div className='bg-white base-border p-5'>
          <div className='text-base font-semibold mb-3 flex items-center gap-2'>
            <i className='bi bi-hourglass-split text-amber-500' />
            {t('profile.pendingResults.title')}
          </div>
          <p className='text-xs text-gray-500 mb-3'>
            {t('profile.pendingResults.description')}
          </p>
          <div className='flex flex-col gap-3'>
            {kpi.items
              .filter((item) => (item?.pending?.length ?? 0) > 0)
              .map((item, idx) => (
                <div
                  key={idx}
                  className='rounded-lg border border-amber-200 bg-amber-50/40 p-3 flex flex-col gap-2'
                >
                  <div className='flex items-center justify-between gap-2'>
                    <span className='font-medium text-sm text-gray-800 truncate'>
                      {item?.kpi?.type?.name || `#${item?.kpi?.type_id}`}
                    </span>
                    <div className='flex flex-col items-end text-xs font-semibold text-amber-700'>
                      {item?.pending_sum > 0 && (
                        <NumericFormat
                          value={item.pending_sum}
                          displayType='text'
                          thousandSeparator=' '
                          decimalScale={2}
                          suffix=' UZS'
                        />
                      )}
                      {item?.pending_valute > 0 && (
                        <NumericFormat
                          value={item.pending_valute}
                          displayType='text'
                          thousandSeparator=' '
                          decimalScale={2}
                          suffix=' $'
                        />
                      )}
                    </div>
                  </div>
                  <div className='flex flex-col gap-1'>
                    {item.pending.map((p, pidx) => {
                      const clickable = !!p.contract_id;
                      return (
                        <div
                          key={pidx}
                          onClick={
                            clickable
                              ? () => openContract(p.contract_id)
                              : undefined
                          }
                          className={`flex items-center justify-between text-xs gap-2 ${
                            clickable
                              ? 'cursor-pointer hover:bg-amber-100 -mx-1 px-1 py-0.5 rounded'
                              : ''
                          }`}
                        >
                          <span
                            className={`truncate flex-1 ${
                              clickable
                                ? 'text-blue-600 hover:underline'
                                : 'text-gray-700'
                            }`}
                          >
                            {p.label || `#${p.id}`}
                          </span>
                          <span className='font-medium text-amber-700 shrink-0'>
                            <NumericFormat
                              value={p.potential}
                              displayType='text'
                              thousandSeparator=' '
                              decimalScale={2}
                              suffix={p.isvalute === 1 ? ' $' : ' UZS'}
                            />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            <div className='flex items-center justify-between rounded-lg bg-amber-100 border border-amber-300 px-3 py-2 mt-1'>
              <span className='font-semibold text-amber-800 text-sm'>
                {t('common.table.total')}:
              </span>
              <div className='flex flex-col items-end font-bold text-amber-800 text-sm'>
                {kpi?.pending_total_sum > 0 && (
                  <NumericFormat
                    value={kpi.pending_total_sum}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                    suffix=' UZS'
                  />
                )}
                {kpi?.pending_total_valute > 0 && (
                  <NumericFormat
                    value={kpi.pending_total_valute}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                    suffix=' $'
                  />
                )}
                {!kpi?.pending_total_sum && !kpi?.pending_total_valute && (
                  <span>0 UZS</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(ProfileStats);
